<template>
  <div data-testid="lgpd-aceites-table">
    <data-table
      ref="dataTableRef"
      :config="datatableConfig"
      :data="acceptances"
      :loading="isLoading"
      :refreshButton="{ show: true }"
      @edit-click="handleRowClick"
      @refresh="loadData"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useLgpdAdmin } from '../../composables/useLgpdAdmin';
import { useFiltro } from '../../composables/useFiltro';
import type { LgpdTermAcceptances_ListRow } from '../../contracts/LgpdTermAcceptances_ListRow';

defineEmits<{
  'view-term': [termId: number];
}>();

const { t } = useI18n();
const { isLoading, listAcceptances } = useLgpdAdmin();
const { getDateRange, init: initFiltro } = useFiltro();

const acceptances = ref<LgpdTermAcceptances_ListRow[]>([]);
const dataTableRef = ref<{ loadData: () => void } | null>(null);

const datatableConfig = computed(() => ({
  tableId: 'lgpdAceitesDataTable',
  columns: [
    // Per app-frontend-service-datatable skill: first column MUST be the
    // ID column with renderType: 'clickable_id'. DataTable's edit-click
    // emits row[column.data], so this column emits the aceite id (number)
    // — wired to handleRowClick which opens the detail modal.
    {
      data: 'id',
      title: t('ID'),
      width: 80,
      renderType: 'clickable_id',
      sortable: true,
    },
    {
      data: 'created_at',
      title: t('Data/hora'),
      width: 180,
      renderType: 'datetime',
      sortable: true,
    },
    {
      data: 'cliente_nome',
      title: t('Cliente'),
      width: 200,
      filterType: 'text',
      sortable: true,
    },
    {
      data: 'cliente_id',
      title: t('ID Cliente'),
      width: 80,
      sortable: true,
    },
    {
      data: 'termo_titulo',
      title: t('Termo'),
      width: 200,
      filterType: 'text',
      sortable: true,
      cellRenderer: ({ value, data }: { value: string; data: LgpdTermAcceptances_ListRow }) => {
        return `${data.termo_versao} - ${value}`;
      },
    },
    {
      data: 'ip_address',
      title: t('IP'),
      width: 140,
      filterType: 'text',
    },
    {
      data: 'recorded_by',
      title: t('Registrado por'),
      width: 150,
      filterType: 'text',
    },
  ],
  pagination: true,
  pageLength: 25,
  showFilters: true,
  filterDisplay: 'row',
  stripedRows: true,
  showGridlines: true,
  size: 'small',
  rowTestId: (data: Record<string, unknown>) => {
    const id = data?.id;
    return id == null ? '' : `lgpd-aceites-row-${id}`;
  },
}));

const loadData = async (): Promise<void> => {
  log('LgpdAceitesTable', 'loadData', 'Loading acceptances scoped by global filter');
  const { startDate, endDate } = getDateRange();
  const filters: Record<string, string | number> = {};
  if (startDate) filters.data_inicio = startDate;
  if (endDate) filters.data_fim = endDate;

  try {
    acceptances.value = await listAcceptances(filters);
  } catch (err) {
    log('LgpdAceitesTable', 'loadData', 'Error', err);
  }
};

const handleRowClick = (aceiteId: number | string): void => {
  // DataTable's edit-click emits the value of the clickable_id column
  // (i.e. the aceite id), not the row object.
  log('LgpdAceitesTable', 'handleRowClick', 'Opening detail modal', aceiteId);
  eventBus.emit('modal.lgpd-aceite.open', { id: aceiteId });
};

const handleFilterUpdated = (): void => {
  log('LgpdAceitesTable', 'handleFilterUpdated', 'Global filter changed, reloading');
  loadData();
};

onMounted(async () => {
  await initFiltro();
  await loadData();
  eventBus.on('filter.updated', handleFilterUpdated);
});

onBeforeUnmount(() => {
  eventBus.off('filter.updated', handleFilterUpdated);
});

defineExpose({ loadData });
</script>
